import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { HelpCircle, X, BookOpen, Video, MessageCircle, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const HelpWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Detect when input fields are focused (for mobile keyboard)
  useEffect(() => {
    if (!isMobile) return;
    
    const handleFocusIn = (e) => {
      const tagName = e.target.tagName.toLowerCase();
      const isFormElement = ['input', 'textarea', 'select'].includes(tagName);
      const isContentEditable = e.target.getAttribute('contenteditable') === 'true';
      
      if ((isFormElement || isContentEditable) && isMobile) {
        setIsInputFocused(true);
        setIsOpen(false); // Close help menu when focusing input
      }
    };

    const handleFocusOut = (e) => {
      const tagName = e.target.tagName.toLowerCase();
      const isFormElement = ['input', 'textarea', 'select'].includes(tagName);
      const isContentEditable = e.target.getAttribute('contenteditable') === 'true';
      
      if (isFormElement || isContentEditable) {
        // Longer delay to ensure we detect if user moved to another input
        setTimeout(() => {
          const activeElement = document.activeElement;
          const activeTagName = activeElement?.tagName.toLowerCase();
          const isActiveFormElement = ['input', 'textarea', 'select'].includes(activeTagName);
          const isActiveContentEditable = activeElement?.getAttribute('contenteditable') === 'true';
          
          if (!isActiveFormElement && !isActiveContentEditable) {
            setIsInputFocused(false);
          }
        }, 300);
      }
    };

    // Use capture phase for more reliable detection
    document.addEventListener('focusin', handleFocusIn, true);
    document.addEventListener('focusout', handleFocusOut, true);
    
    return () => {
      document.removeEventListener('focusin', handleFocusIn, true);
      document.removeEventListener('focusout', handleFocusOut, true);
    };
  }, [isMobile]);

  // Handle visual viewport changes (keyboard open/close on mobile)
  useEffect(() => {
    if (!isMobile) return;
    
    // Only use visualViewport if available
    if (!window.visualViewport) {
      return;
    }

    let lastHeight = window.visualViewport.height;
    
    const handleViewportChange = () => {
      const viewportHeight = window.visualViewport.height;
      const windowHeight = window.innerHeight;
      const heightDiff = windowHeight - viewportHeight;
      
      // If viewport shrunk significantly (keyboard opened)
      if (heightDiff > 150 && viewportHeight < lastHeight) {
        setIsInputFocused(true);
      } 
      // If viewport grew back (keyboard closed)
      else if (heightDiff < 100 && viewportHeight > lastHeight) {
        // Give a small delay before showing widget again
        setTimeout(() => {
          const activeElement = document.activeElement;
          const activeTagName = activeElement?.tagName.toLowerCase();
          if (!['input', 'textarea', 'select'].includes(activeTagName)) {
            setIsInputFocused(false);
          }
        }, 200);
      }
      
      lastHeight = viewportHeight;
    };

    window.visualViewport.addEventListener('resize', handleViewportChange);
    return () => {
      window.visualViewport.removeEventListener('resize', handleViewportChange);
    };
  }, [isMobile]);

  const quickLinks = [
    {
      icon: BookOpen,
      title: 'Help Center',
      description: 'Browse articles and guides',
      action: () => {
        navigate('/help');
        setIsOpen(false);
      }
    },
    {
      icon: Video,
      title: 'Video Tutorials',
      description: 'Watch step-by-step videos',
      action: () => alert('Video tutorials coming soon!')
    },
    {
      icon: RefreshCw,
      title: 'Restart Tour',
      description: 'View the onboarding tour again',
      action: () => {
        localStorage.removeItem('tour_completed_admin');
        localStorage.removeItem('tour_completed_patient');
        window.location.reload();
      }
    },
    {
      icon: MessageCircle,
      title: 'Contact Support',
      description: 'Get help from our team',
      action: () => window.location.href = 'mailto:support@mymedmember.com?subject=Support%20Request'
    }
  ];

  // Don't render button when input is focused on mobile (keyboard is open)
  if (isInputFocused && isMobile) {
    return null;
  }

  // Minimized state - just show a small indicator
  if (isMinimized && !isOpen) {
    return (
      <div className="fixed bottom-4 right-4 z-40 sm:bottom-6 sm:right-6">
        <Button
          onClick={() => setIsMinimized(false)}
          variant="outline"
          size="sm"
          className="h-8 w-8 rounded-full bg-white shadow-md border-teal-200 hover:bg-teal-50 p-0"
          title="Show help button"
        >
          <ChevronUp className="h-4 w-4 text-teal-600" />
        </Button>
      </div>
    );
  }

  return (
    <>
      {/* Help Button - Responsive positioning */}
      <div className={`fixed z-40 transition-all duration-300 ${
        isMobile 
          ? 'bottom-4 right-4' 
          : 'bottom-6 right-6'
      }`}>
        <div className="flex flex-col items-end gap-2">
          {/* Main help button */}
          <Button
            onClick={() => setIsOpen(!isOpen)}
            className={`rounded-full bg-teal-600 hover:bg-teal-700 shadow-lg hover:shadow-xl transition-all ${
              isMobile ? 'w-12 h-12' : 'w-14 h-14'
            }`}
            title="Help & Support"
          >
            {isOpen ? (
              <X className={isMobile ? 'h-5 w-5' : 'h-6 w-6'} />
            ) : (
              <HelpCircle className={isMobile ? 'h-5 w-5' : 'h-6 w-6'} />
            )}
          </Button>
          
          {/* Minimize button - only show on mobile when not open */}
          {isMobile && !isOpen && (
            <Button
              onClick={() => setIsMinimized(true)}
              variant="ghost"
              size="sm"
              className="h-6 w-6 rounded-full bg-white/80 hover:bg-white shadow-sm p-0"
              title="Minimize help button"
            >
              <ChevronDown className="h-3 w-3 text-gray-500" />
            </Button>
          )}
        </div>
      </div>

      {/* Help Menu - Responsive */}
      {isOpen && (
        <div className={`fixed z-40 transition-all duration-300 ${
          isMobile 
            ? 'bottom-20 right-4 left-4 w-auto' 
            : 'bottom-24 right-6 w-80'
        }`}>
          <Card className="shadow-2xl">
            <CardHeader className="bg-teal-600 text-white rounded-t-lg py-3 px-4 sm:py-4 sm:px-6">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base sm:text-lg">How can we help?</CardTitle>
                  <CardDescription className="text-teal-50 text-xs sm:text-sm">
                    Quick access to help resources
                  </CardDescription>
                </div>
                {isMobile && (
                  <Button
                    onClick={() => setIsOpen(false)}
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-white hover:bg-white/20 rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-3 sm:p-4">
              <div className="space-y-1 sm:space-y-2">
                {quickLinks.map((link, idx) => (
                  <button
                    key={idx}
                    onClick={link.action}
                    className="w-full flex items-start gap-2 sm:gap-3 p-2 sm:p-3 hover:bg-gray-50 rounded-lg transition text-left"
                  >
                    <div className="bg-teal-100 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                      <link.icon className="h-4 w-4 sm:h-5 sm:w-5 text-teal-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 text-sm sm:text-base">{link.title}</p>
                      <p className="text-xs sm:text-sm text-gray-600 truncate">{link.description}</p>
                    </div>
                  </button>
                ))}
              </div>

              <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t">
                <p className="text-xs text-gray-500 text-center">
                  Need more help? Email us at{' '}
                  <a href="mailto:support@mymedmember.com" className="text-teal-600 hover:underline">
                    support@mymedmember.com
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default HelpWidget;
